/**
 * Enhanced WebSocket Service for Real-time Code Development
 * Handles connection, reconnection, message routing, and code generation streaming
 */
class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = {};
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000;
    this.isConnecting = false;
    this.projectId = null;
    this.token = null;
    this.roomSubscriptions = new Set();
    this.messageQueue = [];
    this.isOnline = navigator.onLine;
    
    // Monitor network status
    window.addEventListener('online', () => {
      this.isOnline = true;
      if (this.projectId && this.token) {
        this.connect(this.projectId, this.token);
      }
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  /**
   * Connect to WebSocket server
   * @param {string} projectId - Project ID
   * @param {string} token - Authentication token
   */
  connect(projectId, token) {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return Promise.resolve();
    }

    this.projectId = projectId;
    this.token = token;
    this.isConnecting = true;

    return new Promise((resolve, reject) => {
      try {
        const wsUrl = `ws://localhost:8003/ws/${projectId}?token=${token}`;
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected to project:', projectId);
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.emit('connected', { projectId });
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (err) {
            console.error('Error parsing WebSocket message:', err);
          }
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason);
          this.isConnecting = false;
          this.emit('disconnected', { code: event.code, reason: event.reason });
          
          // Attempt to reconnect unless it's a permanent closure
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.isConnecting = false;
          this.emit('error', error);
          reject(error);
        };

      } catch (err) {
        this.isConnecting = false;
        console.error('Error creating WebSocket connection:', err);
        reject(err);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.listeners = {};
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnect() {
    this.reconnectAttempts++;
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
    
    setTimeout(() => {
      if (this.projectId && this.token) {
        this.connect(this.projectId, this.token).catch(err => {
          console.error('Reconnection failed:', err);
        });
      }
    }, this.reconnectInterval * this.reconnectAttempts);
  }

  /**
   * Handle incoming WebSocket messages
   * @param {Object} data - Message data
   */
  handleMessage(data) {
    const { type } = data;
    
    // Process queued messages if connection was just established
    if (type === 'connection_established') {
      this.processMessageQueue();
      // Subscribe to previously subscribed rooms
      this.roomSubscriptions.forEach(room => {
        this.subscribeToRoom(room);
      });
    }
    
    switch (type) {
      case 'connection_established':
        this.emit('connectionEstablished', data);
        break;
        
      case 'file_update':
      case 'file_operation':
        this.emit('fileUpdate', data);
        break;
        
      case 'task_progress':
        this.emit('taskProgress', data);
        break;
        
      case 'workspace_update':
        this.emit('workspaceUpdate', data);
        break;
        
      case 'code_generation':
      case 'code_generation_progress':
      case 'code_generation_complete':
        this.emit('codeGeneration', data);
        break;
        
      case 'system_message':
        this.emit('systemMessage', data);
        break;
        
      case 'room_subscribed':
        console.log('Subscribed to room:', data.room);
        break;
        
      case 'room_unsubscribed':
        console.log('Unsubscribed from room:', data.room);
        break;
        
      case 'pong':
        this.emit('pong', data);
        break;
        
      default:
        console.log('Unknown message type:', type, data);
        this.emit('unknown', data);
        break;
    }
    
    // Emit all messages for generic listeners
    this.emit('message', data);
  }

  /**
   * Send message to server
   * @param {Object} data - Message data
   */
  send(data) {
    if (!this.isOnline) {
      console.warn('Device is offline, queueing message:', data);
      this.messageQueue.push(data);
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket not connected, queueing message:', data);
      this.messageQueue.push(data);
    }
  }

  /**
   * Process queued messages
   */
  processMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.send(message);
    }
  }

  /**
   * Subscribe to a specific room for targeted updates
   * @param {string} room - Room name
   */
  subscribeToRoom(room) {
    this.roomSubscriptions.add(room);
    this.send({
      type: 'subscribe_room',
      room: room
    });
  }

  /**
   * Unsubscribe from a room
   * @param {string} room - Room name
   */
  unsubscribeFromRoom(room) {
    this.roomSubscriptions.delete(room);
    this.send({
      type: 'unsubscribe_room',
      room: room
    });
  }

  /**
   * Send ping to keep connection alive
   */
  ping() {
    this.send({
      type: 'ping',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      this.ping();
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Code Development Specific Methods
   */

  /**
   * Subscribe to code generation updates for a project
   * @param {string} projectId - Project ID
   */
  subscribeToCodeGeneration(projectId) {
    this.subscribeToRoom(`code_generation:${projectId}`);
    this.subscribeToRoom(`project:${projectId}`);
  }

  /**
   * Subscribe to file updates for a project
   * @param {string} projectId - Project ID
   */
  subscribeToFileUpdates(projectId) {
    this.subscribeToRoom(`files:${projectId}`);
    this.subscribeToRoom(`project:${projectId}`);
  }

  /**
   * Subscribe to task progress for a project
   * @param {string} projectId - Project ID
   */
  subscribeToTaskProgress(projectId) {
    this.subscribeToRoom(`tasks:${projectId}`);
    this.subscribeToRoom(`project:${projectId}`);
  }

  /**
   * Subscribe to workspace changes for a project
   * @param {string} projectId - Project ID
   */
  subscribeToWorkspaceUpdates(projectId) {
    this.subscribeToRoom(`workspace:${projectId}`);
    this.subscribeToRoom(`project:${projectId}`);
  }

  /**
   * Subscribe to all project-related updates
   * @param {string} projectId - Project ID
   */
  subscribeToProject(projectId) {
    this.subscribeToCodeGeneration(projectId);
    this.subscribeToFileUpdates(projectId);
    this.subscribeToTaskProgress(projectId);
    this.subscribeToWorkspaceUpdates(projectId);
  }

  /**
   * Request workspace status
   * @param {string} projectId - Project ID
   */
  requestWorkspaceStatus(projectId) {
    this.send({
      type: 'get_workspace_status',
      project_id: projectId
    });
  }

  /**
   * Get connection status
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection state
   */
  getConnectionState() {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'closed';
      default:
        return 'unknown';
    }
  }

  /**
   * Add event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * Emit event to listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error(`Error in WebSocket event listener for ${event}:`, err);
        }
      });
    }
  }

  /**
   * Get connection status (legacy method for backward compatibility)
   * @returns {string} Connection status
   */
  getStatus() {
    return this.getConnectionState();
  }

  /**
   * Get detailed connection info
   */
  getConnectionInfo() {
    return {
      state: this.getConnectionState(),
      projectId: this.projectId,
      isOnline: this.isOnline,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      subscribedRooms: Array.from(this.roomSubscriptions),
      queuedMessages: this.messageQueue.length
    };
  }
}

// Create and export singleton instance
const websocketService = new WebSocketService();
export { websocketService };
export default websocketService;